import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check } from 'lucide-react';
import Seo, { faqLd, serviceLd } from '../../components/Seo';
import Reveal from '../../components/Reveal';
import { useSiteContent } from '../../content/siteContent';

const modes = {
  automatic: {
    label: 'Automatic detection',
    intro: 'Your app reads the incoming call itself and finishes verification in the background. The phone never rings.',
    steps: [
      { verb: 'Request', body: 'Your app sends the user’s mobile number to the VOIP2 platform.' },
      { verb: 'Call', body: 'The platform places a flash call. The calling number is the one-time code.' },
      { verb: 'Read', body: 'The app reads the calling number and sends it back. The call is dropped before it rings.' },
      { verb: 'Confirm', body: 'The platform matches the code and confirms the user, or asks them to try again.' },
    ],
  },
  missed: {
    label: 'Missed call',
    intro: 'Where the app cannot read calls, the user types in digits from the missed call instead.',
    steps: [
      { verb: 'Call', body: 'A flash call is placed to the number the user entered in your app.' },
      { verb: 'Cancel', body: 'As soon as the phone is alerted the call is cancelled, leaving a missed call.' },
      { verb: 'Type', body: 'The user keys in the last 4 to 8 digits of the missed call, as your app asks.' },
    ],
  },
};

const benefits = [
  { title: 'No termination charge', body: 'Calls are designed not to be answered, so in normal operation there is no per-minute cost.' },
  { title: 'Dedicated capacity', body: 'Flash call traffic runs on trunks reserved for it through agreements with mobile networks.' },
  { title: 'Nothing for the user to do', body: 'With automatic detection the phone never rings and the user is never interrupted.' },
];

const rollout = [
  { title: 'Set the baseline', body: 'Start from your current SMS OTP volumes, destinations and costs.' },
  { title: 'Move a first share', body: 'Send an agreed percentage of verifications as flash calls on the same integration.' },
  { title: 'Grow market by market', body: 'Increase the share where verification rates and user experience hold steady.' },
  { title: 'Keep SMS as fallback', body: 'SMS stays in place for the rest of the traffic and any market not yet covered.' },
];

const VoiceOTP = () => {
  const [mode, setMode] = useState('automatic');
  const active = modes[mode];
  const c = useSiteContent('voice-otp');
  const highlight = c('hero.highlight');

  return (
    <div className="min-h-screen bg-white text-[#0a1f2e]">
      <Seo
        title="Voice OTP & Flash Call Verification"
        description="Verify users with a flash call instead of an SMS one-time password. Priced per attempt, with SMS kept as a fallback."
        path="/products/voice-otp"
        breadcrumbs={[{ name: 'Voice OTP & Flash Calls', path: '/products/voice-otp' }]}
        jsonLd={serviceLd({ name: 'Voice OTP & Flash Calls', description: "Verify users with a flash call instead of an SMS one-time password. Priced per attempt, with SMS kept as a fallback.", path: '/products/voice-otp' })}
      />

      {/* Hero */}
      <section className="px-4 pb-20 pt-32 sm:px-6 lg:px-8">
        <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 lg:grid-cols-12">
          <div className="lg:col-span-6">
            <Reveal as="h1" className="font-display text-5xl font-semibold leading-[1.05] tracking-tight md:text-6xl">
              {c('hero.headline')}{highlight && <> <span className="whitespace-nowrap rounded-lg bg-[#c8ff00] px-2">{highlight}</span></>}
            </Reveal>
            <Reveal as="p" delay={120} className="mt-6 max-w-[48ch] text-lg leading-relaxed text-gray-600">
              {c('hero.sub')}
            </Reveal>
            <Reveal delay={240} className="mt-10 flex flex-col gap-4 sm:flex-row">
              <Link to="/contact" className="inline-flex items-center justify-center gap-2 rounded-full bg-[#c8ff00] px-8 py-4 font-semibold text-[#0a1f2e] transition-transform hover:brightness-95 active:scale-[0.98]">
                Get pricing <ArrowRight className="h-5 w-5" />
              </Link>
              <a href="#how-it-works" className="inline-flex items-center justify-center rounded-full border border-[#0a1f2e]/25 px-8 py-4 font-semibold text-[#0a1f2e] transition-colors hover:border-[#0a1f2e] hover:bg-[#0a1f2e] hover:text-white active:scale-[0.98]">
                How it works
              </a>
            </Reveal>
          </div>
          <Reveal delay={150} className="lg:col-span-6">
            <div
              className="flex aspect-[4/3] items-center justify-center rounded-3xl border border-gray-200"
              style={{ background: 'radial-gradient(ellipse at 50% 58%, rgba(200,255,0,0.25), transparent 60%), linear-gradient(to bottom, #f1f6e6, #ffffff)' }}
            >
              <img src="/media/icons/voice-otp.webp" alt="" className="h-3/4 w-3/4 object-contain drop-shadow-[0_30px_40px_rgba(10,31,46,0.25)]" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* Why flash calls */}
      <section className="bg-[#f5f7f9] px-4 py-24 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <Reveal className="mb-12 max-w-3xl">
            <h2 className="font-display text-4xl font-semibold tracking-tight md:text-5xl">{c('intro.title')}</h2>
            <p className="mt-5 text-lg leading-relaxed text-gray-600">
              {c('intro.body')}
            </p>
          </Reveal>
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-12">
            {benefits.map((benefit, i) => (
              <Reveal
                key={benefit.title}
                delay={i * 80}
                className={`rounded-2xl p-8 ${i === 0 ? 'flex flex-col justify-end bg-[#0a1f2e] text-white lg:col-span-6 lg:row-span-2 lg:min-h-[320px]' : 'border border-gray-200 bg-white lg:col-span-6'}`}
              >
                <h3 className={`font-display font-semibold ${i === 0 ? 'text-3xl text-[#c8ff00]' : 'text-2xl'}`}>{benefit.title}</h3>
                <p className={`mt-2 ${i === 0 ? 'text-lg text-gray-300' : 'text-gray-600'}`}>{benefit.body}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how-it-works" className="scroll-mt-20 px-4 py-24 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <Reveal className="mb-10 max-w-2xl">
            <h2 className="font-display text-4xl font-semibold tracking-tight md:text-5xl">How it works</h2>
          </Reveal>
          <div role="tablist" aria-label="Verification modes" className="mb-10 inline-flex rounded-full border border-gray-200 bg-white p-1">
            {Object.entries(modes).map(([key, m]) => (
              <button
                key={key}
                role="tab"
                aria-selected={mode === key}
                onClick={() => setMode(key)}
                className={`rounded-full px-5 py-2.5 text-sm font-semibold transition-colors ${mode === key ? 'bg-[#c8ff00] text-[#0a1f2e]' : 'text-gray-600 hover:text-[#0a1f2e]'}`}
              >
                {m.label}
              </button>
            ))}
          </div>
          <p className="mb-12 max-w-[60ch] text-lg text-gray-600">{active.intro}</p>
          <div className={`grid grid-cols-1 gap-10 md:grid-cols-2 ${active.steps.length === 4 ? 'lg:grid-cols-4' : 'lg:grid-cols-3'}`}>
            {active.steps.map((step, i) => (
              <Reveal key={`${mode}-${step.verb}`} delay={i * 90} className="border-t-2 border-[#c8ff00] pt-6">
                <div className="font-display text-3xl font-semibold">{step.verb}</div>
                <p className="mt-3 text-gray-600">{step.body}</p>
              </Reveal>
            ))}
          </div>
          <p className="mt-12 max-w-[70ch] text-sm text-gray-500">
            If a call is answered by a person or voicemail, the standard per-minute rate applies to that call.
          </p>
        </div>
      </section>

      {/* Pricing model and rollout */}
      <section className="bg-[#f5f7f9] px-4 py-24 sm:px-6 lg:px-8">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-16 lg:grid-cols-2">
          <Reveal>
            <h2 className="font-display text-4xl font-semibold tracking-tight">Priced per attempt, not per minute</h2>
            <p className="mt-5 text-lg leading-relaxed text-gray-600">
              Flash calls are sold in volume bundles of verification attempts. The rate is agreed against your forecast, so cost follows your sign-ups rather than carrier minute rates.
            </p>
            <ul className="mt-8 space-y-4">
              {['One flat rate across most destinations', 'A small number of markets priced separately', 'No setup fee and no separate trial contract'].map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#c8ff00]">
                    <Check className="h-4 w-4 text-[#0a1f2e]" strokeWidth={2.5} />
                  </span>
                  <span className="text-gray-700">{item}</span>
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={120}>
            <h2 className="font-display text-4xl font-semibold tracking-tight">Switch from SMS at your own pace</h2>
            <ol className="mt-8 space-y-6">
              {rollout.map((item, i) => (
                <li key={item.title} className="flex gap-5">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#0a1f2e] font-display font-semibold text-[#c8ff00]">{i + 1}</span>
                  <div>
                    <h3 className="font-semibold">{item.title}</h3>
                    <p className="mt-1 text-gray-600">{item.body}</p>
                  </div>
                </li>
              ))}
            </ol>
          </Reveal>
        </div>
      </section>

      {/* Coverage */}
      <section className="px-4 py-24 sm:px-6 lg:px-8">
        <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-6">
            <div className="font-display text-7xl font-semibold md:text-8xl">200+</div>
            <h2 className="mt-2 font-display text-3xl font-semibold">countries reachable</h2>
            <p className="mt-5 max-w-[52ch] text-lg leading-relaxed text-gray-600">
              Flash calls work across most of the world, with three or more mobile networks reachable in the large majority of countries. The service is not yet available in the US, Canada or China.
            </p>
            <Link to="/contact" className="mt-8 inline-flex items-center gap-2 font-semibold text-[#0a1f2e] underline decoration-[#c8ff00] decoration-2 underline-offset-4 hover:decoration-[#0a1f2e]">
              Ask for the full country list <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
          <Reveal delay={120} className="lg:col-span-6">
            <div className="overflow-hidden rounded-3xl bg-[#0a1f2e]">
              <img src="/media/site/did-international.webp" alt="Globe showing countries reachable by flash calls" loading="lazy" className="aspect-[4/3] w-full object-cover" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="px-4 pb-24 sm:px-6 lg:px-8">
        <Reveal className="mx-auto max-w-7xl rounded-3xl bg-[#0a1f2e] px-6 py-20 text-center text-white">
          <h2 className="font-display text-4xl font-semibold tracking-tight md:text-5xl">See what you would save</h2>
          <p className="mx-auto mt-5 max-w-[52ch] text-lg text-gray-300">
            Send us your monthly verification volumes and country mix. We will compare flash call pricing with your current SMS spend.
          </p>
          <Link to="/contact" className="mt-10 inline-flex items-center justify-center gap-2 rounded-full bg-[#c8ff00] px-8 py-4 font-semibold text-[#0a1f2e] transition-transform hover:brightness-95 active:scale-[0.98]">
            Get pricing <ArrowRight className="h-5 w-5" />
          </Link>
        </Reveal>
      </section>
    </div>
  );
};

export default VoiceOTP;
